import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import { Box, InputLabel, Paper, useMediaQuery, useTheme } from "@mui/material";
import { getService } from "../../reducers/service";
import { notify } from "../../reducers/NotifierReducer";
import ReOrderedSequence from "./ReOrderedSequence";
import { getExampleID } from "./customIDHelper";

const fieldMargin = "0 4px 8px";
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& h4": {
      marginBottom: theme.spacing(2),
    },
  },
  scrollablePaper: {
    maxHeight: "80vh",
    overflowY: "auto",
    padding: "16px",
  },
  form: {
    margin: "0 -4px 4px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    flexGrow: 1,
  },
  formControl: {
    minWidth: "210px",
  },
  selectEmpty: {
    height: "40px",
  },
  item: {
    gridStyle(minWidth, maxWidth) {
      return {
        maxWidth: maxWidth,
        flexBasis: minWidth,
        minWidth: minWidth,
        flexGrow: 1,
        margin: fieldMargin,
      };
    },
    margin: 0,
  },
  actionBtnStyle: {
    textAlign: "right",
    paddingRight: "20px",
    position: "sticky",
    top: "15px",
    right: "20px",
  },
}));

export const defaultValues = {
  Application: "AMS",
  Sequence: 0,
  SequenceLength: 5,
  PrefixOrder: 1,
  SuffixOrder: 0,
  MonthOrder: 0,
  DayOrder: 0,
  YearOrder: 0,
  SequenceOrder: 2,
  SequenceResetType: "N",
  UseSeparator: false,
  IsDeleted: false,
  Prefix: "",
  Suffix: "",
  UseFourDigitYear: false,
  Sequencer: 0,
  Separator: "",
};

function CustomIDGenerator(props) {
  const classes = useStyles();
  const theme = useTheme();
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const agenciesList = dictionary.ptsAgencies;

  const service = getService("pts-custom-agency-id");
  const { initialData, dataForNewItem, entityName, displayName } = props;
  const defaultData = { ...defaultValues, ...dataForNewItem };

  const [getRow, setGetRow] = useState(null);
  const dbId = { ...initialData };
  const [exId, setExId] = useState("");
  const [createNew, setCreateNew] = useState("");
  const [currentOrder, setCurrentOrder] = useState(0);

  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const computedHeight = isTablet
    ? "calc(100vh - 90px)"
    : isSmall
    ? "calc(100vh - 155px)"
    : "calc(100vh - 100px)";

  const fetchData = async (dbId) => {
    service
      .get(dbId)
      .then((res) => {
        if (res && res.length > 0) {
          setGetRow(res[0]);
        } else {
          setGetRow({
            ...defaultData,
            AgencyID: dbId.AgencyID,
            Application: "AMS",
            EntityName: entityName,
          });
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (dbId?.AgencyID) {
      fetchData({
        AgencyID: dbId.AgencyID,
        Application: "AMS",
        EntityName: entityName,
      });
    }
  }, [dbId?.AgencyID]);

  useEffect(() => {
    if (getRow) {
      const example = getExampleID(getRow);
      setExId(example);
    }
  }, [getRow]);

  useEffect(() => {
    if (!getRow) {
      setCreateNew("Create A New Entry Below");
      setGetRow({
        ...defaultData,
        AgencyID: "",
        Application: "AMS",
        EntityName: entityName,
      });
    } else if (getRow && getRow.Created) setCreateNew("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRow]);

  const handleResetChange = (event) => {
    const targetValue = event.target.value;
    if (targetValue === "") setGetRow({ ...getRow, SequenceResetType: "N" });
    else setGetRow({ ...getRow, SequenceResetType: targetValue });
  };

  const handlePrefixCheckboxChange = () => {
    if (getRow.PrefixOrder === 0) setGetRow({ ...getRow, PrefixOrder: 1 });
    else setGetRow({ ...getRow, PrefixOrder: 0 });
  };

  const handleMonthCheckboxChange = () => {
    if (getRow.MonthOrder !== 0) setGetRow({ ...getRow, MonthOrder: 0 });
    else {
      setGetRow({ ...getRow, MonthOrder: currentOrder + 1 });
    }
  };

  const handleDayCheckboxChange = () => {
    if (getRow.DayOrder !== 0) setGetRow({ ...getRow, DayOrder: 0 });
    else {
      setGetRow({ ...getRow, DayOrder: currentOrder + 1 });
    }
  };

  const handleYearCheckboxChange = () => {
    if (getRow.YearOrder !== 0) setGetRow({ ...getRow, YearOrder: 0 });
    else {
      setGetRow({ ...getRow, YearOrder: currentOrder + 1 });
    }
  };

  const handleSuffixCheckboxChange = () => {
    if (getRow.SuffixOrder !== 0) setGetRow({ ...getRow, SuffixOrder: 0 });
    else {
      setGetRow({ ...getRow, SuffixOrder: currentOrder + 1 });
    }
  };

  const handleUse4digitsCheckboxChange = () => {
    if (getRow.UseFourDigitYear)
      setGetRow({ ...getRow, UseFourDigitYear: false });
    else setGetRow({ ...getRow, UseFourDigitYear: true });
  };

  const handleUseSeparatorChange = () => {
    if (getRow.UseSeparator) setGetRow({ ...getRow, UseSeparator: false });
    else setGetRow({ ...getRow, UseSeparator: true });
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (getRow && getRow.Separator === "") getRow.Separator = null;
    try {
      if (!getRow.Created) {
        service
          .create(getRow)
          .then((res) => props.notify("Custom ID Saved!", "success"))
          .catch((err) => props.notify("Save Failed!", "error"));
      } else {
        const {
          RecordID,
          Application,
          Created,
          CreatedBy,
          Deleted,
          DeletedBy,
          DeletedIP,
          EntityName,
          Flags,
          IsDeleted,
          LastUsed,
          ROWGUID,
          Updated,
          UpdatedBy,
          UpdatedIP,
          ...rest
        } = getRow;

        service
          .patch(dbId, rest)
          .then((res) => props.notify("Custom ID Saved!", "success"))
          .catch((err) => props.notify("Save Failed!", "error"));
      }
      await fetchData(dbId);
    } catch (error) {
      console.error("error", error);
    }
  };

  const prefixError1 = getRow?.Prefix?.length > 9;
  const prefixError2 = getRow?.PrefixOrder === 1 && !getRow?.Prefix;
  const sequenceLengthError =
    getRow?.SequenceLength > 100 || getRow?.SequenceLength < 4;
  const suffixError1 = getRow?.Suffix?.length > 9;
  const suffixError2 = getRow?.SuffixOrder !== 0 && !getRow?.Suffix;
  const separatorError1 = getRow?.Separator?.length > 1;
  const separatorError2 = getRow?.UseSeparator === true && !getRow?.Separator;
  const sequencerError = getRow?.Sequencer > 9223372036854775807;

  const disableSave = () => {
    return (
      prefixError1 ||
      prefixError2 ||
      sequenceLengthError ||
      suffixError1 ||
      suffixError2 ||
      separatorError1 ||
      separatorError2 ||
      sequencerError
    );
  };

  const prefixHelperText = () => {
    if (prefixError1) return "Prefix can not be more than 9 letters!";
    if (prefixError2) return "Prefix can not be empty!";
    return "";
  };

  const suffixHelperText = () => {
    if (suffixError1) return "Suffix can not be more than 9 letters!";
    if (suffixError2) return "Suffix can not be empty!";
    return "";
  };

  const separatorHelperText = () => {
    if (separatorError1) return "Single Character only!";
    if (separatorError2) return "Separator can not be empty!";
    return "";
  };

  const renderAgencySelector = () => (
    <FormControl
      variant="outlined"
      size="small"
      sx={{ width: 250, color: "white", mr: 1.5 }}
    >
      <InputLabel id="users-agency" shrink>
        Agency
      </InputLabel>
      <Select
        labelId="users-agency"
        id="agency-select"
        label="Agency"
        value={getRow?.AgencyID || ""}
        onChange={(e) => setGetRow({ ...getRow, AgencyID: e.target.value })}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 200,
              overflow: "auto",
            },
          },
        }}
      >
        {agenciesList?.map((a) => (
          <MenuItem key={a.AgencyID} value={a.AgencyID}>
            {a.AgencyID}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Paper sx={{ height: computedHeight }}>
      <div className={classes.actionBtnStyle}>
        <Button
          type="submit"
          onClick={handleSave}
          disabled={disableSave()}
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
        >
          <SaveIcon /> Save
        </Button>
      </div>
      <Box
        sx={{ margin: 2, height: isSmall ? "580px" : "740px" }}
        className={classes.scrollablePaper}
      >
        <div className="mb-4">
          <Typography variant="h6" color="primary">
            {createNew}
          </Typography>
        </div>
        {renderAgencySelector()}
        <h4 className="mb-4 mt-3">Custom ID For {displayName || ""}</h4>
        {getRow && (
          <ReOrderedSequence
            row={getRow}
            setGetRow={setGetRow}
            setCurrentOrder={setCurrentOrder}
          />
        )}
        <Typography variant="body1" gutterBottom className="mt-4 mb-4">
          <span className="mr-5">Example Identifier:</span>
          <span className="ml-4">{exId}</span>
        </Typography>
        <Grid container>
          <Grid item md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={getRow?.PrefixOrder !== 0}
                  onChange={handlePrefixCheckboxChange}
                  name="prefix"
                  color="primary"
                />
              }
              label="Prefix"
            />
          </Grid>

          <Grid item>
            <TextField
              error={prefixError1 || prefixError2}
              helperText={prefixHelperText()}
              label="Text"
              variant="outlined"
              value={getRow ? getRow.Prefix : ""}
              required={getRow?.PrefixOrder === 1}
              disabled={getRow?.PrefixOrder === 0}
              onChange={(e) => setGetRow({ ...getRow, Prefix: e.target.value })}
              size="small"
            />
          </Grid>
        </Grid>
        <Grid container className="mt-2">
          <Grid item md={2}>
            <Typography variant="body1" gutterBottom>
              Sequencer Length
            </Typography>
          </Grid>
          <Grid item md={3}>
            <TextField
              error={sequenceLengthError}
              helperText={
                sequenceLengthError
                  ? "Sequence should be between 4 and 100"
                  : ""
              }
              label="Number"
              type="number"
              value={getRow ? getRow.SequenceLength : 5}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              onChange={(e) =>
                setGetRow({ ...getRow, SequenceLength: e.target.value })
              }
              size="small"
            />
          </Grid>
        </Grid>
        <Grid container className="mt-3 mb-3">
          <Grid item md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={getRow?.MonthOrder !== 0}
                  onChange={handleMonthCheckboxChange}
                  name="month"
                  color="primary"
                />
              }
              label="Month"
            />
          </Grid>

          <Grid item md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={getRow?.DayOrder !== 0}
                  onChange={handleDayCheckboxChange}
                  name="day"
                  color="primary"
                />
              }
              label="Day"
            />
          </Grid>

          <Grid item md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={getRow?.YearOrder !== 0}
                  onChange={handleYearCheckboxChange}
                  name="year"
                  color="primary"
                />
              }
              label="Year"
            />

            <span className="mr-5"></span>

            <FormControlLabel
              control={
                <Checkbox
                  checked={getRow?.UseFourDigitYear === true}
                  onChange={handleUse4digitsCheckboxChange}
                  name="use4digits"
                  color="primary"
                  disabled={getRow?.YearOrder === 0}
                />
              }
              label="Use 4 digits"
            />
          </Grid>

          <Grid container item md={12}>
            <Grid item md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={getRow?.SuffixOrder !== 0}
                    onChange={handleSuffixCheckboxChange}
                    name="suffix"
                    color="primary"
                  />
                }
                label="Suffix"
              />
            </Grid>
            <Grid item>
              <TextField
                error={suffixError1 || suffixError2}
                helperText={suffixHelperText()}
                label="Text"
                value={getRow && getRow.Suffix ? getRow.Suffix : ""}
                variant="outlined"
                required={getRow?.SuffixOrder !== 0}
                disabled={getRow?.SuffixOrder === 0}
                size="small"
                onChange={(e) =>
                  setGetRow({ ...getRow, Suffix: e.target.value })
                }
              />
            </Grid>
          </Grid>

          <Grid container item md={12}>
            <Grid item md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={getRow?.UseSeparator === true}
                    onChange={handleUseSeparatorChange}
                    name="includeSeparator"
                    color="primary"
                  />
                }
                label="Include Separator"
              />
            </Grid>
            <Grid item>
              <TextField
                error={separatorError1 || separatorError2}
                helperText={separatorHelperText()}
                label="Character"
                onChange={(e) =>
                  setGetRow({ ...getRow, Separator: e.target.value })
                }
                variant="outlined"
                value={getRow && getRow.Separator ? getRow.Separator : ""}
                required={getRow?.UseSeparator === true}
                disabled={getRow?.UseSeparator === false}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container className="mt-3 mb-3">
          <Grid item md={2}>
            <Typography variant="body1" gutterBottom>
              Reset Interval
            </Typography>
          </Grid>
          <Grid item>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth={true}
              className={classes.formControl}
            >
              <Select
                value={getRow ? getRow.SequenceResetType : ""}
                onChange={handleResetChange}
                size="small"
                className={classes.selectEmpty}
                inputProps={{ "aria-label": "Without label" }}
              >
                <MenuItem value="N">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="D">Day</MenuItem>
                <MenuItem value="M">Month</MenuItem>
                <MenuItem value="Y">Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container className="mt-3 mb-3">
          <Grid item md={2}>
            <Typography variant="body1" gutterBottom>
              Last Used Sequencer
            </Typography>
          </Grid>
          <Grid item>
            <TextField
              error={sequencerError}
              helperText={
                sequencerError ? "Should not put more than 18 digits!" : ""
              }
              label="Number"
              value={getRow && getRow.Sequencer ? getRow.Sequencer : ""}
              type="number"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              onChange={(e) =>
                setGetRow({ ...getRow, Sequencer: e.target.value })
              }
              size="small"
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

const mapStateToProps = (state) => ({
  dictionary: state.dictionary,
});

export default connect(mapStateToProps, {
  notify,
})(CustomIDGenerator);
